/**
 * Wallet — every user (customer/agent/employee) has one.
 * Shows balance in £, lifetime credited/debited, transaction history, and
 * a request-payout button.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Wallet as WalletIcon, ArrowDownRight, ArrowUpRight, Send, Coins, Clock, ExternalLink, FileDown } from "lucide-react";
import { toast } from "sonner";
import TreasureCodeWidget from "@/components/TreasureCodeWidget";

function money(n) {
  return `£${(Number(n) || 0).toFixed(2)}`;
}

function TxRow({ tx }) {
  const isCredit = tx.kind === "credit";
  return (
    <div className="flex items-start justify-between py-3 border-b border-border last:border-b-0" data-testid={`wallet-tx-${tx.id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isCredit ? "bg-emerald-500/15 text-emerald-500" : "bg-orange-500/15 text-orange-500"}`}>
          {isCredit ? <ArrowDownRight className="w-4 h-4" /> : <ArrowUpRight className="w-4 h-4" />}
        </div>
        <div>
          <div className="font-medium text-sm">{tx.reason}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{new Date(tx.at).toLocaleString("en-GB")}</div>
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className={`font-mono font-semibold text-sm ${isCredit ? "text-emerald-500" : "text-foreground"}`}>
          {isCredit ? "+" : "−"}{money(tx.amount_gbp)}
        </div>
        <div className="text-[10px] text-muted-foreground">bal {money(tx.balance_after)}</div>
      </div>
    </div>
  );
}

export default function Wallet() {
  const [wallet, setWallet] = useState(null);
  const [txns, setTxns] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPayout, setShowPayout] = useState(false);
  const [form, setForm] = useState({ amount_gbp: "", method: "bank_transfer", bank_name: "", bank_sort_code: "", bank_account_last4: "", note: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [w, t, p] = await Promise.all([
        api.get("/wallet"),
        api.get("/wallet/transactions?limit=50"),
        api.get("/wallet/payouts"),
      ]);
      setWallet(w.data);
      setTxns(t.data.transactions || []);
      setPayouts(p.data.payouts || []);
    } catch (e) { toast.error("Failed to load wallet"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(form.amount_gbp);
    if (!amount || amount <= 0) return toast.error("Enter a valid amount");
    if (form.method === "bank_transfer" && (!form.bank_name || !form.bank_account_last4)) {
      return toast.error("Bank name and last-4 required for bank transfer");
    }
    setSubmitting(true);
    try {
      await api.post("/wallet/payout", { ...form, amount_gbp: amount });
      toast.success("Payout request submitted");
      setShowPayout(false);
      setForm({ amount_gbp: "", method: "bank_transfer", bank_name: "", bank_sort_code: "", bank_account_last4: "", note: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payout failed");
    }
    setSubmitting(false);
  };

  if (loading) return <div className="p-8 text-center text-muted-foreground">Loading wallet…</div>;
  if (!wallet) return <div className="p-8 text-center text-muted-foreground">Wallet unavailable</div>;

  const downloadStatement = async (month) => {
    try {
      const url = month
        ? `/wallet/statement?month=${month}`
        : "/wallet/statement";
      const res = await api.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `treasurescare-wallet-${month || new Date().toISOString().slice(0, 7)}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      toast.error("Statement download failed");
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="wallet-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground overline">
            <WalletIcon className="w-4 h-4" /> Your Treasurescare Wallet
          </div>
          <h1 className="text-3xl font-display font-light mt-1">My Wallet</h1>
        </div>
        <Dialog open={showPayout} onOpenChange={setShowPayout}>
          <DialogTrigger asChild>
            <Button size="lg" data-testid="wallet-request-payout-btn" disabled={wallet.balance_gbp <= 0}>
              <Send className="w-4 h-4 mr-2" /> Request Payout
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Request payout</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label>Amount (£)</Label>
                <Input type="number" step="0.01" max={wallet.balance_gbp}
                  value={form.amount_gbp} onChange={(e) => setForm(f => ({ ...f, amount_gbp: e.target.value }))}
                  placeholder={`Max £${wallet.balance_gbp.toFixed(2)}`}
                  data-testid="payout-amount-input" required />
              </div>
              <div>
                <Label>Method</Label>
                <Select value={form.method} onValueChange={(v) => setForm(f => ({ ...f, method: v }))}>
                  <SelectTrigger data-testid="payout-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank transfer (UK)</SelectItem>
                    <SelectItem value="credit_balance">Keep as credit balance</SelectItem>
                    <SelectItem value="stripe_connect">Stripe Connect</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.method === "bank_transfer" && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label>Bank name</Label>
                    <Input value={form.bank_name} onChange={(e) => setForm(f => ({ ...f, bank_name: e.target.value }))} data-testid="payout-bank-name" />
                  </div>
                  <div>
                    <Label>Sort code</Label>
                    <Input value={form.bank_sort_code} placeholder="12-34-56" onChange={(e) => setForm(f => ({ ...f, bank_sort_code: e.target.value }))} data-testid="payout-bank-sort" />
                  </div>
                  <div>
                    <Label>Account last 4</Label>
                    <Input value={form.bank_account_last4} maxLength={4} placeholder="1234" onChange={(e) => setForm(f => ({ ...f, bank_account_last4: e.target.value }))} data-testid="payout-bank-last4" />
                  </div>
                </div>
              )}
              <div>
                <Label>Note (optional)</Label>
                <Textarea rows={2} value={form.note} onChange={(e) => setForm(f => ({ ...f, note: e.target.value }))} data-testid="payout-note" />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={submitting} data-testid="payout-submit-btn">
                  {submitting ? "Submitting…" : "Submit request"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Balance card */}
      <Card className="bg-primary text-primary-foreground overflow-hidden relative">
        <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-secondary/20 blur-3xl" />
        <CardContent className="pt-6 relative z-10">
          <div className="text-[10px] uppercase tracking-widest opacity-70">Current balance</div>
          <div className="text-5xl font-mono font-light mt-1" data-testid="wallet-balance">
            {money(wallet.balance_gbp)}
          </div>
          <div className="mt-4 flex flex-wrap gap-4 text-xs">
            <div><span className="opacity-70">Lifetime earned </span><span className="font-mono">{money(wallet.lifetime_credited_gbp)}</span></div>
            <div><span className="opacity-70">Lifetime paid out </span><span className="font-mono">{money(wallet.lifetime_debited_gbp)}</span></div>
            <div><span className="opacity-70">Status </span><span className="uppercase font-semibold">{wallet.status}</span></div>
          </div>
        </CardContent>
      </Card>

      {/* Pending payouts */}
      {payouts.filter(p => p.status === "pending").length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Pending payout requests</CardTitle></CardHeader>
          <CardContent>
            {payouts.filter(p => p.status === "pending").map(p => (
              <div key={p.id} className="flex items-center justify-between py-2 border-b border-border last:border-b-0" data-testid={`payout-pending-${p.id}`}>
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-amber-500" />
                  <div>
                    <div className="text-sm font-medium">{money(p.amount_gbp)} via {p.method.replace("_", " ")}</div>
                    <div className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleDateString("en-GB")}</div>
                  </div>
                </div>
                <Badge variant="outline">Awaiting admin approval</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Treasure Code — vanity referral share code */}
      <TreasureCodeWidget />

      {/* Transactions */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2"><Coins className="w-4 h-4" /> Transaction history</CardTitle>
          <Button size="sm" variant="outline" onClick={() => downloadStatement()} data-testid="download-statement-btn">
            <FileDown className="w-3.5 h-3.5 mr-1.5" /> Statement PDF
          </Button>
        </CardHeader>
        <CardContent>
          {txns.length === 0
            ? <div className="text-center py-8 text-muted-foreground text-sm">No transactions yet. Once money flows into your wallet, you'll see it here.</div>
            : txns.map(tx => <TxRow key={tx.id} tx={tx} />)}
        </CardContent>
      </Card>
    </div>
  );
}
